import { Address, AddressType, getDefaultPrefix, LockType, } from './address';
import { generateCkbAddressString } from '../utils';
import { validators, transformers, normalizers } from '../ckb-js-toolkit';
import { SerializeScript } from '../ckb-lumos/core';
import { Blake2bHasher } from '../hashers';
import { NervosAddressVersion } from '../helpers/address';
import { CHAIN_SPECS } from '../constants';
export class Script {
    constructor(codeHash, args, hashType) {
        this.codeHash = codeHash;
        this.args = args;
        this.hashType = hashType;
    }
    static fromRPC(data) {
        if (!data)
            return undefined;
        validators.ValidateScript(data);
        return new Script(data.code_hash, data.args, data.hash_type);
    }
    identifyLockType() {
        for (const config of Object.values(CHAIN_SPECS)) {
            // Default Lock
            if (this.sameCodeTypeWith(config.defaultLock.script)) {
                return LockType.default;
            }
            // MultiSig Lock
            else if (this.sameCodeTypeWith(config.multiSigLock.script)) {
                return LockType.multisig;
            }
            // PW-Lock
            else if (this.sameCodeTypeWith(config.pwLock.script)) {
                return LockType.pw;
            }
            // Omni Lock
            else if (Object.prototype.hasOwnProperty.call(config, 'omniLock') &&
                this.sameCodeTypeWith(config.omniLock.script)) {
                return LockType.omni;
            }
            // ACP Lock
            else if (Object.prototype.hasOwnProperty.call(config, 'acpLock') &&
                this.sameCodeTypeWith(config.acpLock.script)) {
                return LockType.acp;
            }
        }
        // No match was found. Unknown Lock.
        return null;
    }
    /**
     * Checks if the code hash and hash type match in the specified script.
     *
     * Note: This ignores the args during the comparison.
     *
     * @param script The script to compare against.
     * @returns True if the codeHash and hashType match.
     */
    sameCodeTypeWith(script) {
        validators.ValidateScript(transformers.TransformScript(script));
        return (this.codeHash === script.codeHash && this.hashType === script.hashType);
    }
    sameWith(script) {
        validators.ValidateScript(transformers.TransformScript(script));
        return (this.args === script.args &&
            this.codeHash === script.codeHash &&
            this.hashType === script.hashType);
    }
    validate() {
        validators.ValidateScript(transformers.TransformScript(this));
        return true;
    }
    serializeJson() {
        return {
            code_hash: this.codeHash,
            args: this.args,
            hash_type: this.hashType,
        };
    }
    toHash() {
        return new Blake2bHasher()
            .update(SerializeScript(normalizers.NormalizeScript(transformers.TransformScript(this))))
            .digest()
            .serializeJson();
    }
    toAddress(prefix = getDefaultPrefix(), addressVersion = NervosAddressVersion.latest) {
        return new Address(generateCkbAddressString(this, prefix, addressVersion), AddressType.ckb);
    }
}
//# sourceMappingURL=data:application/json;base64,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